// src/PublicGallery.tsx
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import UserAvatar from "../components/UserAvatar";
import LanguageSwitcher from "../components/LanguageSwitcher";
import LikeHeart from "../components/LikeHeart";
import ShareButton from "../components/ShareButton";
import { Toaster, toast } from "../components/toaster";
import CTAButton from "../components/CTAButton";
import MissingImage from "../components/MissingImage";
import MobileNav from "../components/MobileNav";
import { dict, getInitialLang, type Lang } from "../lib/i18n";
import { initTG, getAvatarUrl, getUserName, vibrateTick, vibrateSuccess } from "../lib/telegram";
import { buildShareUrl, likeImage, fetchPublicGallery, type PublicGalleryItem } from "../lib/api";
import NavBar from "../components/NavBar";


function SmartImage({ src, alt, className }: { src?: string | null; alt: string; className?: string }) {
  const [broken, setBroken] = useState<boolean>(() => !src);
  useEffect(() => setBroken(!src), [src]);
  if (broken) return <MissingImage title={alt} className={className} />;
  return <img src={src as string} alt={alt} loading="lazy" className={className} onError={() => setBroken(true)} />;
}

// Универсальная 36×36 икон-кнопка (визуально совпадает с Publish/Share)
function IconBtn({
  title,
  onClick,
  children,
  className = "",
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={
        "inline-flex h-9 w-9 items-center justify-center rounded-full " +
        "border border-white/15 bg-white/5 hover:bg-white/10 " +
        "focus:outline-none focus:ring-2 focus:ring-pink-400/60 transition " +
        className
      }
    >
      {children}
    </button>
  );
}

function useIo(cb: () => void, enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && cb(), { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [cb, enabled]);
  return ref;
}

export default function PublicGallery() {
  // язык / header
  const [lang, setLang] = useState<Lang>(() => getInitialLang(initTG()?.initDataUnsafe?.user?.language_code ?? null));
  const t = (k: keyof typeof dict["ru"]) => dict[lang][k];
  const name = getUserName();
  const avatar = getAvatarUrl();

  // состояние
  const [items, setItems] = useState<PublicGalleryItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);                 // безопасный лимит, чтобы не ловить 422 от бэка
  const [order, setOrder] = useState<"new" | "top">("new");
  const [query, setQuery] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<PublicGalleryItem | null>(null);
  const tg = useMemo(() => initTG(), []);
  const tgId: number | undefined = tg?.initDataUnsafe?.user?.id ?? undefined;

  const loadingRef = useRef(false);
  const bootRef = useRef(true);

  // загрузка страницы
  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await fetchPublicGallery(page, limit, order, query);
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return prev.concat(res.items.filter((x) => !seen.has(x.id)));
      });
      const more = page * res.limit < res.total;
      setHasMore(more);
      if (more) setPage((p) => p + 1);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      if (bootRef.current) bootRef.current = false;
    }
  }, [page, limit, order, query]);

  // первая загрузка
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // при смене сортировки/поиска перезагружаем ленту с 1-й страницы
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(false);
    bootRef.current = true;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, query]);

  const sentinelRef = useIo(() => { if (!bootRef.current && hasMore) load(); }, !bootRef.current && hasMore);

  const handleLike = async (id: number, uid?: number) => {
  vibrateTick();
  try {
    await likeImage(id, uid); // uid может быть undefined, и likeImage это поддерживает
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, likes: x.likes + 1 } : x)));
    vibrateSuccess();
  } catch {}
};

  const toggleLang = () => {
    setLang((l) => {
      const n = l === "ru" ? "uz" : "ru";
      localStorage.setItem("lang", n);
      return n;
    });
  };

  return (
    <div className="min-h-screen-svh bg-grid relative text-white">
      <Toaster />
      <div className="absolute inset-0 grid-lines pointer-events-none"></div>

      {/* HEADER */}
      <div
          className="
      absolute left-1/2 -translate-x-1/2
      z-20
      top-[calc(env(safe-area-inset-top))]
    "
      >
        <div
            className="
          inline-flex items-center gap-2 text-[10px] md:text-xs
          font-semibold px-3 py-1 rounded-full glass
        "
        >
          <span className="size-2 rounded-full bg-emerald-400"></span>
          {t("public_title")}
        </div>
      </div>

      <header
          className="
        tg-safe-header
        relative z-10 max-w-6xl mx-auto
        h-12 md:h-16 flex items-center justify-between
        px-3 md:px-4
      "
      >
        <div className="flex items-center gap-2">
          <UserAvatar name={name} src={avatar ?? undefined}/>
          <span className="text-xs md:text-sm text-white/70">
      {dict[lang].welcome} {name}
    </span>
        </div>

        <div className="hidden md:flex">
          <NavBar/>
        </div>


        <div className="flex items-center gap-2">
          <LanguageSwitcher value={lang} onToggle={toggleLang}/>
          <a className="text-xs md:text-sm text-white/70 hover:text-white transition"
             href="https://github.com/lexaowner" target="_blank" rel="noreferrer">GitHub</a>
          <a className="text-xs md:text-sm text-white/70 hover:text-white transition" href="https://t.me/EMWCL" target="_blank" rel="noreferrer">TG</a>
        </div>
      </header>

      <MobileNav>
        <NavBar compact />
      </MobileNav>

      {/* MAIN */}
      <main className="relative z-10 max-w-6xl mx-auto px-3 md:px-4 pt-6 md:pt-8">
        <div className="flex items-center justify-between mb-4 gap-3">
          {/* Поиск + сортировка */}
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search_placeholder")}
                className="w-full shrink min-w-0 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="relative shrink-0">
              <select
                value={order}
                onChange={(e) => setOrder(e.target.value as "new" | "top")}
                className="appearance-none rounded-xl bg-white/5 border border-white/10 pl-3 pr-8 py-2 text-sm"
              >
                <option value="new">{t("sort_new")}</option>
                <option value="top">{t("sort_top")}</option>
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-white/60">▾</span>
            </div>
          </div>
        </div>

        {/* Колонки с карточками */}
        <div className="columns-2 md:columns-3 gap-3 [column-fill:_balance]">
          {items.map((it) => (
            <article
              key={it.id}
              className="mb-3 rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition
                         shadow-[0_10px_30px_rgba(0,0,0,.35)] min-w-0 [break-inside:avoid-column]"
            >
              <button className="block w-full" onClick={() => setPreview(it)} aria-label="Открыть превью">
                <SmartImage src={it.image_url} alt={it.prompt} className="w-full h-auto object-cover rounded-b-none" />
              </button>

              <div className="p-3 space-y-2 min-w-0">
                <p className="text-sm text-white/85 line-clamp-2">{it.prompt}</p>
                <div className="text-[12px] text-white/50">
                  {it.owner_username ? `@${it.owner_name}` : t("user_anon")}
                </div>

                {/* Действия */}
                <div className="pt-1.5 flex items-center justify-between gap-2">
                  {/* Лайк */}
                  <LikeHeart
                    id={it.id}
                    userId={tgId}
                    initialCount={it.likes}
                    onLike={async () => handleLike(it.id, tgId)}
                    vibrateTick={vibrateTick}
                    vibrateSuccess={vibrateSuccess}
                    compact
                  />

                  <div className="flex items-center gap-1">
                    {/* Копировать промпт */}
                    <IconBtn
                      title="Скопировать промпт"
                      onClick={async () => {
                        try {
                          await (navigator.clipboard?.writeText(it.prompt) ?? Promise.reject());
                        } catch {
                          const ta = document.createElement("textarea");
                          ta.value = it.prompt;
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand("copy");
                          ta.remove();
                        }
                        vibrateTick();
                        toast(t("copy_prompt"), "success")
                      }}
                    >
                      {/* простой «копия» символ чтобы не тянуть лишние иконки */}
                      <svg viewBox="0 0 24 24" className="h-4 w-4 text-white/85" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                        <rect x="2" y="2" width="13" height="13" rx="2"></rect>
                      </svg>
                    </IconBtn>

                    {/* Поделиться: одинаковый размер, превью и поднятый лист */}
                    <ShareButton
                      title={it.owner_username ? `@${it.owner_username}` : t("share_gallery")}
                      text={it.prompt}
                      url={buildShareUrl(it.id)}
                      previewSrc={it.image_url ?? undefined}
                      compact
                      bottomOffset={88}
                      vibrateTick={vibrateTick}
                      vibrateSuccess={vibrateSuccess}
                      className="text-white/80 hover:text-white"
                    />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Подгрузка / пусто */}
        <div ref={sentinelRef} className="h-12 flex items-center justify-center">
          {loading && <div className="text-white/60 text-sm animate-pulse">{t("loading")}</div>}
          {!loading && !hasMore && items.length === 0 && <div className="text-white/60 text-sm">{t("empty")}</div>}
          {!loading && !hasMore && items.length > 0 && <div className="text-white/40 text-xs">{t("no_more")}</div>}
        </div>
      </main>

      {/* Модалка превью */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <SmartImage src={preview.image_url} alt={preview.prompt} className="w-full h-auto rounded-2xl" />
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-sm text-white/80 line-clamp-2">{preview.prompt}</p>
              <div className="flex items-center gap-2">
                <CTAButton
                  onClick={async () => {
                    try {
                      await (navigator.clipboard?.writeText(preview.prompt) ?? Promise.reject());
                    } catch {
                      const ta = document.createElement("textarea");
                      ta.value = preview.prompt;
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand("copy");
                      ta.remove();
                    }
                    vibrateTick();
                    toast("Промпт скопирован", "success");
                  }}
                >
                  {t("copy_prompt")}
                </CTAButton>
                <CTAButton onClick={() => setPreview(null)}>{t("close")}</CTAButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
